// Report shape used by the customer-facing report page. buildReport() renders
// demo data from a local Case; buildReportFromBundle() populates the same
// shape from real Supabase records so the report reflects the processed
// invoice instead of placeholder fixtures.
import { resolveVendorName } from "@/lib/invoice/vendor-identity";
import { resolveCanonicalRecovery } from "@/lib/report/canonical-recovery";
import { confidenceBandFromFraction, toConfidencePct } from "@/lib/confidence/confidence-vocabulary";

import type { Case } from "./cases";
import type {
  Review,
  InvoiceData,
  FindingItem,
  AIFindings,
  LegalResearch,
  Report as StoredReport,
} from "./db/models";

export interface Finding {
  item: string;
  charge: number;
  issue: string;
  impact: "Low" | "Medium" | "High";
  savings: [number, number];
}

export interface Report {
  invoiceTotal: number;
  fairRange: [number, number];
  potentialSavings: [number, number];
  savingsPct: [number, number];
  /**
   * CANONICAL customer-facing recovery figure. Bound directly to the finalized
   * ai_findings.estimated_savings record (legacy potential_savings as fallback).
   * Never recomputed from market benchmarks, charge breakdowns or finding item
   * amounts — those remain explanatory evidence only. Every surface that shows
   * "Estimated Recovery" must read this field so Dashboard, Notifications and
   * every report package reconcile to $0.00.
   */
  estimatedSavings: number;

  strength: "Low" | "Medium" | "High" | "Very High";
  confidence: number;
  vendor: string;
  invoiceDate: string;
  invoiceType: string;
  zip: string;
  state: string;
  findings: Finding[];
  laws: string[];
  violations: string[];
  timeline: { label: string; value: string }[];
  nextSteps: string[];
}

export const MISSING_FIELD = "Missing";
export const NOT_EXTRACTED = "Not extracted";

export interface InvoiceReportFields {
  invoiceDataId: string | null;
  vendorName: string;
  invoiceNumber: string;
  invoiceDate: string;
  invoiceType: string;
  totalDue: number | null;
  zip: string;
  city: string;
  state: string;
  serviceAddress: string;
  lineItems: Array<Record<string, unknown>>;
  lineItemCount: number;
}

export function buildReport(c: Case): Report {
  const total = parseFloat(c.details.totalAmount ?? "") || 0;
  const low = Math.round(total * 0.71);
  const high = Math.round(total * 0.8);
  const savingsLow = total - high;
  const savingsHigh = total - low;
  return {
    invoiceTotal: total,
    fairRange: [low, high],
    potentialSavings: [savingsLow, savingsHigh],
    estimatedSavings: Math.round(savingsHigh),

    savingsPct: [
      total > 0 ? Math.round((savingsLow / total) * 100) : 0,
      total > 0 ? Math.round((savingsHigh / total) * 100) : 0,
    ],
    strength: "High",
    confidence: 91,
    vendor: c.details.vendor || MISSING_FIELD,
    invoiceDate: c.details.invoiceDate || NOT_EXTRACTED,
    invoiceType: c.details.invoiceType || NOT_EXTRACTED,
    zip: c.details.zip || MISSING_FIELD,
    state: MISSING_FIELD,
    findings: [
      {
        item: "Storage Rate (28 days)",
        charge: Math.round(total * 0.86),
        issue: "Rate exceeds local market average by 43%",
        impact: "High",
        savings: [820, 1120],
      },
      {
        item: "Administrative Fee",
        charge: 125,
        issue: "Not authorized under state transportation code",
        impact: "High",
        savings: [125, 125],
      },
      {
        item: "Environmental Fee",
        charge: 75,
        issue: "No documentation or justification provided",
        impact: "Medium",
        savings: [50, 75],
      },
      {
        item: "Lien Fee",
        charge: 15,
        issue: "Appears to duplicate other charges",
        impact: "Low",
        savings: [10, 15],
      },
    ],
    laws: [
      "Applicable state tow & storage lien statute",
      "Applicable state non-consent tow rate rules",
      "Applicable state property code — notice requirements",
      "State Deceptive Trade Practices / UDAP statute",
    ],
    violations: [
      "Storage period exceeds statutory limits",
      "Rate exceeds reasonable market value",
      "Administrative fee not authorized",
      "Missing required release documentation",
    ],
    timeline: [
      { label: "Laws & regulations reviewed", value: "7" },
      { label: "Questionable charges identified", value: "5" },
      { label: "Local rate comparisons", value: "12" },
      { label: "Storage days analyzed", value: "28" },
    ],
    nextSteps: [
      "Review & understand the findings and supporting information.",
      "Negotiate with the vendor using our talking points and legal citations.",
      "Escalate if needed — our experts can take over on your behalf.",
    ],
  };
}

export const currency = (n: number) =>
  n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
  });

/* ────────────────── Real-data builder ────────────────── */

export interface RealReportBundle {
  review: Review | null;
  invoiceData?: InvoiceData | null;
  findings: AIFindings | null;
  findingItems: FindingItem[];
  legalResearch: LegalResearch[];
  /** Documented market benchmark rows, used as the evidence floor for recovery. */
  marketResearch?: { status?: string | null; difference_amount?: number | null }[];
  /** Charge-level market comparisons — the certified recovery scope. */
  marketComparisons?: unknown[];
  report?: StoredReport | null;
}


function pickString(o: Record<string, unknown> | undefined, keys: string[]): string | undefined {
  if (!o) return undefined;
  for (const k of keys) {
    const v = o[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return undefined;
}

function pickNumber(o: Record<string, unknown> | undefined, keys: string[]): number | undefined {
  if (!o) return undefined;
  for (const k of keys) {
    const v = o[k];
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string") {
      const n = Number(v.replace(/[^0-9.\-]/g, ""));
      if (Number.isFinite(n) && n > 0) return n;
    }
  }
  return undefined;
}

function moneyFromText(raw: unknown, labels: RegExp[]): number | undefined {
  if (typeof raw !== "string" || !raw.trim()) return undefined;
  for (const label of labels) {
    const match = raw.match(label);
    if (!match?.index && match?.index !== 0) continue;
    const window = raw.slice(match.index, match.index + 140);
    const amounts = [...window.matchAll(/\$?\s*([0-9][0-9,]*(?:\.\d{2})?)/g)]
      .map((m) => Number(m[1].replace(/,/g, "")))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (amounts.length) return Math.max(...amounts);
  }
  return undefined;
}

function bestTotalDue(extracted: Record<string, unknown> | undefined, review: Review | null): number | null {
  const explicit = pickNumber(extracted, [
    "total_due",
    "totalDue",
    "amount_due",
    "amountDue",
    "balance_due",
    "balanceDue",
    "invoice_total",
    "invoiceTotal",
  ]);
  const rawTotal = moneyFromText(extracted?.raw_text, [/total\s+due/i, /amount\s+due/i, /balance\s+due/i]);
  const extractedTotal = pickNumber(extracted, ["total_amount", "totalAmount"]);
  const reviewTotal = typeof review?.total_amount === "number" ? review.total_amount : undefined;
  const candidates = [explicit, rawTotal, extractedTotal, reviewTotal].filter(
    (n): n is number => typeof n === "number" && Number.isFinite(n) && n > 0,
  );
  if (!candidates.length) return null;
  // Prefer a labeled "total due"/"amount due" value. Otherwise use the largest
  // credible total so a subtotal/net amount does not overwrite the amount owed.
  return explicit ?? rawTotal ?? Math.max(...candidates);
}

export function extractInvoiceReportFields(
  invoiceData: InvoiceData | null | undefined,
  review: Review | null,
): InvoiceReportFields {
  const extracted = invoiceData?.extracted as Record<string, unknown> | undefined;
  const location = extracted?.location as Record<string, unknown> | undefined;
  const lineItems = Array.isArray(invoiceData?.line_items)
    ? (invoiceData?.line_items as Array<Record<string, unknown>>)
    : Array.isArray(extracted?.line_items)
      ? (extracted?.line_items as Array<Record<string, unknown>>)
      : [];

  return {
    invoiceDataId: invoiceData?.id ?? null,
    // Vendor identity is resolved once at extraction time (entity_roles);
    // never fall back to a name the classifier assigned to owner/customer.
    vendorName: resolveVendorName(extracted, review?.vendor) ?? MISSING_FIELD,
    invoiceNumber:
      pickString(extracted, ["invoice_number", "invoiceNumber", "invoice_id", "invoiceId"]) ??
      NOT_EXTRACTED,
    invoiceDate:
      pickString(extracted, ["invoice_date", "invoiceDate"]) ??
      review?.invoice_date ??
      NOT_EXTRACTED,
    invoiceType:
      pickString(extracted, ["invoice_type", "invoiceType"]) ??
      review?.invoice_type ??
      NOT_EXTRACTED,
    totalDue: bestTotalDue(extracted, review),
    zip:
      pickString(location, ["zip5", "zip", "postal_code", "postalCode"]) ??
      pickString(extracted, ["zip_code", "zipCode", "zip"]) ??
      review?.zip ??
      MISSING_FIELD,
    city: pickString(location, ["city", "locality"]) ?? pickString(extracted, ["city"]) ?? MISSING_FIELD,
    state:
      pickString(location, ["state", "region"]) ??
      pickString(extracted, ["state"]) ??
      MISSING_FIELD,
    serviceAddress:
      pickString(location, ["normalized_address", "address"]) ??
      pickString(extracted, ["service_address", "serviceAddress", "supplier_address"]) ??
      NOT_EXTRACTED,
    lineItems,
    lineItemCount: lineItems.length,
  };
}

function lineItemFinding(li: Record<string, unknown>): Finding | null {
  const description = pickString(li, ["description", "name", "label", "item"]);
  const amount = pickNumber(li, ["total", "amount", "line_total", "extended", "price"]);
  if (!description || !amount) return null;
  const hay = description.toLowerCase();
  const questionable = /storage|admin|duplicate|environment|cleanup|lien|surcharge|convenience|supervision|notice|release/.test(hay);
  if (!questionable) return null;
  const quantity = pickNumber(li, ["quantity", "qty"]);
  const unit = pickNumber(li, ["unitPrice", "unit_price", "rate"]);
  const issue = quantity && unit
    ? `Review ${quantity} × ${currency(unit)} against invoice support and local benchmarks.`
    : "Review this charge against invoice support and local benchmarks.";
  return {
    item: description,
    charge: amount,
    issue,
    impact: /storage|duplicate|admin|lien/.test(hay) ? "High" : "Medium",
    savings: [Math.round(amount * 0.5), Math.round(amount)],
  };
}

function severityToImpact(s: string | null | undefined): "Low" | "Medium" | "High" {
  const v = (s ?? "").toLowerCase();
  if (v === "critical" || v === "high") return "High";
  if (v === "medium" || v === "moderate") return "Medium";
  return "Low";
}

function strengthFromConfidence(c: number | null | undefined): Report["strength"] {
  // SAB-004: delegates to the canonical confidence ladder. Accepts both the
  // stored 0..1 fraction and a legacy 0..100 value so the strength badge can
  // never contradict the displayed confidence percentage.
  const band = confidenceBandFromFraction(c);
  if (band === "Very High") return "Very High";
  if (band === "High") return "High";
  if (band === "Moderate") return "Medium";
  return "Low";
}


/**
 * Populate the customer-facing Report shape from real Supabase records.
 * Falls back to values on `fallback` (typically buildReport(caseFromReview))
 * for any field the pipeline hasn't produced yet — so a partially-completed
 * review still renders without demo vendor/total leaking through.
 */
export function buildReportFromBundle(
  bundle: RealReportBundle,
  fallback: Report,
): Report {
  const review = bundle.review;
  if (!review) return fallback;
  const invoice = extractInvoiceReportFields(bundle.invoiceData, review);

  const total = invoice.totalDue ?? 0;

  const derivedFindings = bundle.invoiceData
    ? invoice.lineItems.map(lineItemFinding).filter((f): f is Finding => !!f)
    : [];

  // ── CANONICAL FINANCIAL SOURCE OF TRUTH ──────────────────────────────
  // All customer-facing financial values originate from the DETERMINISTIC
  // charge-level recovery engine (canonical-recovery.ts → deterministic-
  // recovery.ts). AI-authored `estimated_savings` is never authoritative; it
  // is passed only so the resolver can report that it was ignored.
  const canonicalRecovery = resolveCanonicalRecovery({
    findingsSavings: (() => {
      const f = bundle.findings;
      const primary = typeof f?.estimated_savings === "number" ? f.estimated_savings : null;
      const legacy = typeof f?.potential_savings === "number" ? f.potential_savings : null;
      return primary ?? legacy;
    })(),
    chargeInputs: {
      lineItems: (bundle.invoiceData?.line_items ?? []) as Array<Record<string, unknown>>,
      marketComparisons: (bundle.marketComparisons ?? []) as never,
      marketResearch: (bundle.marketResearch ?? []) as never,
    },
    marketRows: bundle.marketResearch ?? [],
    invoiceTotal: total > 0 ? total : null,
  });
  const canonicalSavings = canonicalRecovery.basis === "none" ? null : canonicalRecovery.amount;


  // Deterministic fair-payment band. The AI never authors this range; it is
  // derived from the documented benchmark bounds of the supported scope.
  const canonicalFair = (() => {
    const { fairPaymentLow, fairPaymentHigh } = canonicalRecovery;
    if (fairPaymentLow === null || fairPaymentHigh === null) return null;
    return [Math.min(fairPaymentLow, fairPaymentHigh), Math.max(fairPaymentLow, fairPaymentHigh)] as [
      number,
      number,
    ];
  })();


  // Only when the pipeline has produced NO finalized findings value at all do
  // we fall back to explanatory evidence so a partially-processed review still
  // renders something. A finalized record always wins.
  const evidenceFallback = (() => {
    const itemSavings = bundle.findingItems.reduce(
      (s, f) => s + (typeof f.amount === "number" && f.amount > 0 ? f.amount : 0),
      0,
    );
    if (itemSavings > 0) return Math.round(itemSavings);
    const derivedSavings = derivedFindings.reduce((s, f) => s + f.savings[1], 0);
    return derivedSavings > 0 ? Math.round(derivedSavings) : 0;
  })();

  const estimatedSavings = Math.min(
    canonicalSavings ?? evidenceFallback,
    total > 0 ? Math.round(total) : Number.POSITIVE_INFINITY,
  );

  /* Reconciliation guard. The canonical recovery figure wins over the
     model's fair range: a stored range that does not satisfy
     invoiceTotal - fairHigh = estimatedSavings would print two conflicting
     numbers on the same page. When they disagree we keep the MODEL'S BAND
     WIDTH but re-anchor it to the canonical recovery, so the report stays
     internally consistent to the cent. */
  const anchoredHigh = Math.max(0, Math.round(total) - estimatedSavings);
  const fairReconciles =
    canonicalFair !== null && Math.abs(canonicalFair[1] - anchoredHigh) <= 1;
  const bandWidth = canonicalFair ? Math.max(0, canonicalFair[1] - canonicalFair[0]) : 0;

  const fairHigh = canonicalFair
    ? fairReconciles
      ? canonicalFair[1]
      : anchoredHigh
    : anchoredHigh;
  const fairLow = canonicalFair
    ? fairReconciles
      ? canonicalFair[0]
      : Math.max(0, fairHigh - bandWidth)
    : anchoredHigh;

  // The displayed savings band is derived FROM the canonical numbers (never the
  // reverse) and always contains the canonical recovery figure.
  const savingsLow = Math.min(estimatedSavings, Math.max(0, Math.round(total) - fairHigh));
  const savingsHigh = Math.max(estimatedSavings, Math.max(0, Math.round(total) - fairLow));

  const dbFindings: Finding[] = bundle.findingItems.map((f) => {
        const amt = typeof f.amount === "number" ? f.amount : 0;
        return {
          item: f.title,
          charge: amt,
          issue: f.description ?? "Reviewed against local benchmarks and applicable rules.",
          impact: severityToImpact(f.severity),
          savings: [Math.round(amt * 0.7), Math.round(amt)],
        };
      });

  const seen = new Set(dbFindings.map((f) => f.item.toLowerCase()));
  const findings: Finding[] = [
    ...dbFindings,
    ...derivedFindings.filter((f) => !seen.has(f.item.toLowerCase())),
  ];

  const laws = bundle.legalResearch.length
    ? bundle.legalResearch.map((l) => l.title ?? l.topic ?? l.statute ?? "Legal reference")
    : [];

  return {
    invoiceTotal: total,
    fairRange: [fairLow, fairHigh],
    potentialSavings: [savingsLow, savingsHigh],
    estimatedSavings,
    savingsPct: [
      total > 0 ? Math.round((savingsLow / total) * 100) : 0,
      total > 0 ? Math.round((savingsHigh / total) * 100) : 0,
    ],

    strength: strengthFromConfidence(bundle.findings?.confidence ?? null),
    // Stored as a 0..1 fraction; tolerate a legacy 0..100 value without
    // rendering an impossible 9,500% confidence. 0 means "no signal".
    confidence: toConfidencePct(bundle.findings?.confidence ?? null) ?? 0,
    vendor: invoice.vendorName,
    invoiceDate: invoice.invoiceDate,
    invoiceType: invoice.invoiceType,
    zip: invoice.zip,
    state: invoice.state,
    findings,
    laws,
    violations: findings.map((f) => f.item),
    timeline: [
      { label: "Laws & regulations reviewed", value: String(bundle.legalResearch.length || 0) },
      { label: "Questionable charges identified", value: String(bundle.findingItems.length || 0) },
      { label: "Findings summary", value: (bundle.findings?.summary ?? "").slice(0, 40) || "—" },
    ],
    nextSteps: fallback.nextSteps,
  };
}

